import { useMemo } from 'react';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import isEmpty from 'lodash/isEmpty';
import { START_NODE_ID } from '@jira/project-settings__project-configuration-commons/src/model/constants.tsx';
import { STATUS_HEIGHT, START_NODE_RADIUS, PORT_RADIUS } from './ellipsis.tsx';
import {
	type DiagramStatus,
	type UnitDirection,
	type DiagramTransition,
	type Point,
	type PortInfo,
	PortIndex,
	toPortInfo,
	toPortKey,
	allStatusPorts,
	toPoint,
} from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { fireError } from './fireError.tsx';
import type { DragDropState } from './useCanvasDragDropEvents.tsx';
import { useLatest } from './useLatest.tsx';
import { useSelectedTransitionInfo } from './useSelectedTransitionInfo.tsx';
import { useStatusesThatShouldShowPorts } from './useStatusesThatShouldShowPorts.tsx';
import type { ProposedTransition } from './useTransitionProposals.tsx';
import type { ReroutingTransition } from './ReroutingTransitionValid.types.tsx';

export type PortByStatusAndIndex = (statusId: string, portIndex: number) => PortInfo;

/** Distance between Status boundary and Port's center */
const PORT_STATUS_OFFSET = 3;
/** Fraction of a Status' width where PortIndeces `Top` and `Bottom` appear */
const PORT_TOP_OR_BOTTOM_X_FRACTION = 1 / 2;
/** Fraction of a Status' width where PortIndeces `TopLeft` and `BottomLeft` appear */
const PORT_TOPLEFT_OR_BOTTOMLEFT_X_FRACTION = 1 / 4.5;
/** Fraction of a Status' width where PortIndeces `TopRight` and `BottomRight` appear */
const PORT_TOPRIGHT_OR_BOTTOMRIGHT_X_FRACTION = 1 / 1.28;

/** Determines the Port's position with reference to the Status it is connected to and its width. */
export function getPortPosition(portIndex: PortIndex, statusPosition: Point, statusWidth: number) {
	switch (portIndex) {
		case PortIndex.Left:
			return toPoint(statusPosition.x - PORT_STATUS_OFFSET, statusPosition.y + STATUS_HEIGHT / 2);

		case PortIndex.TopLeft:
			return toPoint(
				statusPosition.x + statusWidth * PORT_TOPLEFT_OR_BOTTOMLEFT_X_FRACTION,
				statusPosition.y - PORT_STATUS_OFFSET,
			);

		case PortIndex.Top:
			return toPoint(
				statusPosition.x + statusWidth * PORT_TOP_OR_BOTTOM_X_FRACTION,
				statusPosition.y - PORT_STATUS_OFFSET,
			);

		case PortIndex.TopRight:
			return toPoint(
				statusPosition.x + statusWidth * PORT_TOPRIGHT_OR_BOTTOMRIGHT_X_FRACTION,
				statusPosition.y - PORT_STATUS_OFFSET,
			);

		case PortIndex.Right:
			return toPoint(
				statusPosition.x + statusWidth + PORT_STATUS_OFFSET,
				statusPosition.y + STATUS_HEIGHT * PORT_TOP_OR_BOTTOM_X_FRACTION,
			);

		case PortIndex.BottomRight:
			return toPoint(
				statusPosition.x + statusWidth * PORT_TOPRIGHT_OR_BOTTOMRIGHT_X_FRACTION,
				statusPosition.y + STATUS_HEIGHT + PORT_STATUS_OFFSET,
			);

		case PortIndex.Bottom:
			return toPoint(
				statusPosition.x + statusWidth * PORT_TOP_OR_BOTTOM_X_FRACTION,
				statusPosition.y + STATUS_HEIGHT + PORT_STATUS_OFFSET,
			);

		case PortIndex.BottomLeft:
			return toPoint(
				statusPosition.x + statusWidth * PORT_TOPLEFT_OR_BOTTOMLEFT_X_FRACTION,
				statusPosition.y + STATUS_HEIGHT + PORT_STATUS_OFFSET,
			);

		default:
			fireError(
				'PortIndex given does not match any known PortIndexes.',
				'use-ports.utils.get-port-position',
				{ portIndex },
			);
			return toPoint(0, 0);
	}
}

interface UsePortsConnectedToSelectedTransitionOptions {
	selectedItem: ItemReference | undefined;
	transitions: DiagramTransition[];
}

/** Returns the Ports that are connected to the selected transition if there is one */
export const usePortsConnectedToSelectedTransition = ({
	selectedItem,
	transitions,
}: UsePortsConnectedToSelectedTransitionOptions) => {
	const selectedTransitionInfo = useSelectedTransitionInfo({
		selectedItem,
		transitions,
	});

	const focusedPortKeys = useMemo(() => {
		if (!selectedTransitionInfo) return [];

		return compact([
			...selectedTransitionInfo.sourcePortIndexKeys,
			...selectedTransitionInfo.targetPortIndexKeys,
		]);
	}, [selectedTransitionInfo]);

	return focusedPortKeys;
};

interface UsePortsOptions {
	hoveredItem: ItemReference | undefined;
	selectedItem: ItemReference | undefined;
	startNodePosition: Point | undefined;
	transitions: DiagramTransition[];
	statuses: DiagramStatus[];
	defaultPositions: Record<string, Point>;
	dragDropState: DragDropState; // TODO: HIX-3914 - pending dragDropState rework
	statusWidths: Record<string, number>;
	proposedTransition: ProposedTransition;
	reroutingTransition: ReroutingTransition;
	isImmutable: boolean;
}

/**
 * Unlike Transitions and Statuses, Ports information is dynamically created.
 * This hook generates them where they are expected given the state of the diagram.
 *
 * This also means Ports don't necessarily have an ID, thus we use PortKeys which
 * are simply formatted as `'StatusId:PortIndex'` in most circumstances */
export const usePorts = ({
	hoveredItem,
	selectedItem,
	transitions,
	statusWidths,
	startNodePosition,
	statuses,
	defaultPositions,
	dragDropState,
	proposedTransition,
	reroutingTransition,
	isImmutable,
}: UsePortsOptions) => {
	const isProposingTransition = proposedTransition.stage !== 'none';

	let glowingPortKey: string | undefined;
	if (proposedTransition.stage === 'valid') {
		glowingPortKey = proposedTransition.endPortKey;
	} else if (reroutingTransition.stage === 'valid') {
		glowingPortKey = reroutingTransition.proposedPortKey;
	}

	const proposedTransitionStartPort =
		proposedTransition.stage === 'none' ? undefined : proposedTransition.startPortKey;

	const statusesThatShouldShowPorts = useStatusesThatShouldShowPorts({
		hoveredItem,
		statuses,
		dragDropState,
		isProposingTransition,
		reroutingTransition,
	});

	const portsConnectedToSelectedTransition = usePortsConnectedToSelectedTransition({
		selectedItem,
		transitions,
	});

	const latestStatuses = useLatest(statuses);

	const portsByPortKey = useMemo(() => {
		const ports: Record<string, PortInfo> = {};

		if (latestStatuses.current.length === 0 || isEmpty(statusWidths)) return ports;

		const startNodePositionOrDefault = startNodePosition || defaultPositions[START_NODE_ID];

		if (startNodePositionOrDefault) {
			ports[START_NODE_ID] = toPortInfo(
				START_NODE_ID,
				PortIndex.Bottom,
				{
					x: startNodePositionOrDefault.x + START_NODE_RADIUS,
					y: startNodePositionOrDefault.y + START_NODE_RADIUS * 2 - PORT_RADIUS,
				},
				false,
				false,
			);
		}

		return latestStatuses.current.reduce((acc, status) => {
			const statusPosition = status.position || defaultPositions[status.id];

			if (statusPosition === undefined) {
				return acc;
			}

			const isVisibleForStatus = statusesThatShouldShowPorts.includes(status.id);

			allStatusPorts.forEach((portIndex) => {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const portKey = toPortKey(status.id, portIndex)!;

				acc[portKey] = {
					key: portKey,
					isVisible:
						(isVisibleForStatus || portsConnectedToSelectedTransition.includes(portKey)) &&
						proposedTransitionStartPort !== portKey,
					statusId: status.id,
					portIndex,
					position: getPortPosition(portIndex, statusPosition, statusWidths[status.id]),
					isGlowing: glowingPortKey === portKey,
				};
			});

			return acc;
		}, ports);
	}, [
		latestStatuses,
		statusWidths,
		startNodePosition,
		defaultPositions,
		statusesThatShouldShowPorts,
		portsConnectedToSelectedTransition,
		proposedTransitionStartPort,
		glowingPortKey,
	]);

	const visiblePorts = useMemo(() => {
		if (isImmutable) {
			return [];
		}

		return filter(portsByPortKey, (x) => x.isVisible);
	}, [isImmutable, portsByPortKey]);

	return { ports: portsByPortKey, visiblePorts };
};

/** Converts PortIndex to a {@link UnitDirection} with respect to which side the port exists on a status */
export function toSquareUnitDirection(portIndex: PortIndex): UnitDirection {
	switch (portIndex) {
		case PortIndex.Top:
		case PortIndex.TopRight:
		case PortIndex.TopLeft:
			return toPoint(0, 1);
		case PortIndex.Bottom:
		case PortIndex.BottomRight:
		case PortIndex.BottomLeft:
			return toPoint(0, -1);
		case PortIndex.Right:
			return toPoint(-1, 0);
		case PortIndex.Left:
			return toPoint(1, 0);
		default:
			return toPoint(0, 0);
	}
}
